import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { customerOrderHistoryApi } from '../api/customerOrderHistoryApi';
import { PageSyncMeta } from '../components/sync/PageSyncMeta';
import { useSyncStatus } from '../contexts/SyncStatusContext';
import { resetFiltersToDefault } from '../utils/filterReset';
import {
  CUSTOMER_ORDER_HISTORY_DEFAULT_FILTERS,
  buildCustomerOrderHistoryFiltersFromSearchParams,
} from '../constants/customerOrderHistoryFilters';

const PAGE_SIZE = 100;

const CustomerOrderHistoryPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { refreshSyncStatus } = useSyncStatus();

  const initialFilters = buildCustomerOrderHistoryFiltersFromSearchParams(searchParams);

  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);

  const offsetRef = useRef(0);
  const observerTarget = useRef(null);
  const observerRef = useRef(null);
  const stateRef = useRef({ loadingMore: false, hasMore: true, loading: true });
  const appliedFiltersRef = useRef(appliedFilters);

  useEffect(() => {
    stateRef.current = { loadingMore, hasMore, loading };
  }, [loadingMore, hasMore, loading]);

  useEffect(() => {
    appliedFiltersRef.current = appliedFilters;
  }, [appliedFilters]);

  const updateSearchParams = (newFilters) => {
    const params = new URLSearchParams();
    if (newFilters.textQuery) {
      params.set('text_query', newFilters.textQuery);
    }
    if (newFilters.textExact) {
      params.set('text_exact', 'true');
    }
    if (newFilters.startDate) {
      params.set('start_date', newFilters.startDate);
    }
    if (newFilters.endDate) {
      params.set('end_date', newFilters.endDate);
    }
    setSearchParams(params, { replace: true });
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return '';
    return num.toLocaleString('ja-JP');
  };

  const formatPrice = (price) => {
    if (price === null || price === undefined) return '';
    const n = Number(price);
    if (!Number.isFinite(n)) return '';

    // 小数第2位までが .00 のときは整数表示（float変換誤差対策で cent 単位に丸める）
    const cents = Math.round(n * 100);
    if (cents % 100 === 0) {
      return (cents / 100).toLocaleString('ja-JP', { maximumFractionDigits: 0 });
    }

    return n.toLocaleString('ja-JP', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    });
  };

  const formatCustomerDisp = (value) => {
    if (value === null || value === undefined) return '-';
    const trimmed = String(value).trim();
    return trimmed || '-';
  };

  const loadInitialItems = useCallback(async () => {
    try {
      setLoading(true);
      setLoadError(null);
      offsetRef.current = 0;
      const response = await customerOrderHistoryApi.getList(
        PAGE_SIZE,
        0,
        appliedFilters
      );
      const rows = Array.isArray(response) ? response : response?.data || [];
      setItems(rows);
      offsetRef.current = rows.length;
      setHasMore(rows.length === PAGE_SIZE);
    } catch (error) {
      console.error('得意先別実績一覧取得エラー:', error);
      setLoadError('得意先別実績一覧の取得に失敗しました');
    } finally {
      setLoading(false);
    }
  }, [appliedFilters]);

  useEffect(() => {
    loadInitialItems();
  }, [loadInitialItems]);

  const handleRefresh = async () => {
    await Promise.all([loadInitialItems(), refreshSyncStatus()]);
  };

  const loadMoreItems = async () => {
    const currentState = stateRef.current;
    if (currentState.loadingMore || !currentState.hasMore || currentState.loading) {
      return;
    }

    try {
      setLoadingMore(true);
      const currentOffset = offsetRef.current;
      const currentAppliedFilters = appliedFiltersRef.current;
      const response = await customerOrderHistoryApi.getList(
        PAGE_SIZE,
        currentOffset,
        currentAppliedFilters
      );
      const rows = Array.isArray(response) ? response : response?.data || [];
      setItems((prev) => [...prev, ...rows]);
      offsetRef.current = currentOffset + rows.length;
      setHasMore(rows.length === PAGE_SIZE);
    } catch (error) {
      console.error('追加データ取得エラー:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  useEffect(() => {
    if (items.length === 0) {
      return undefined;
    }

    if (!observerRef.current) {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (!entry) return;
          const currentState = stateRef.current;
          if (
            entry.isIntersecting &&
            currentState.hasMore &&
            !currentState.loadingMore &&
            !currentState.loading
          ) {
            loadMoreItems();
          }
        },
        { threshold: 0, rootMargin: '500px' }
      );
    }

    const observer = observerRef.current;
    const target = observerTarget.current;
    if (target) {
      observer.observe(target);
    }

    return () => {
      if (target) {
        observer.unobserve(target);
      }
    };
  }, [items.length, hasMore, loading, loadingMore]);

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleFilterClear = (field) => {
    setFilters((prev) => ({ ...prev, [field]: CUSTOMER_ORDER_HISTORY_DEFAULT_FILTERS[field] ?? '' }));
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    setAppliedFilters({ ...filters });
    updateSearchParams(filters);
  };

  const handleClearAll = () => {
    resetFiltersToDefault({
      defaults: CUSTOMER_ORDER_HISTORY_DEFAULT_FILTERS,
      setFilters,
      setAppliedFilters,
      updateSearchParams,
    });
  };

  const columns = [
    {
      header: '期',
      render: (row) => row.ki || '-',
    },
    {
      header: '発注日',
      render: (row) => row.hacchu_date || '-',
    },
    {
      header: '資材発注No/受注No',
      render: (row) => (
        <div className="text-slate-900 whitespace-normal break-words leading-snug">
          <div>{row.hacchu_no || '-'}</div>
          <div className="text-slate-600">{row.juchu_no || '-'}</div>
        </div>
      ),
    },
    {
      header: '得意先CD/得意先',
      render: (row) => (
        <div className="text-slate-900 whitespace-normal break-words leading-snug">
          <div>{formatCustomerDisp(row.customer_disp)}</div>
          <div className="text-slate-600">{row.customer_name || '-'}</div>
        </div>
      ),
    },
    {
      header: '製品CD',
      render: (row) => row.product_disp || '-',
    },
    {
      header: '製品名/寸法',
      render: (row) => (
        <div className="text-slate-900 whitespace-normal break-words leading-snug">
          <div>{row.product_name || '-'}</div>
          <div className="text-slate-600">{row.product_size || '-'}</div>
        </div>
      ),
    },
    {
      header: '発注数量（単位）',
      render: (row) => {
        const amount = formatNumber(row.hacchu_amount);
        const tani = row.tani ? `（${row.tani}）` : '';
        return `${amount}${tani}`;
      },
    },
    {
      header: '仕入単価',
      render: (row) =>
        row.stanka !== null && row.stanka !== undefined
          ? formatPrice(row.stanka)
          : '-',
    },
    {
      header: '販売単価',
      render: (row) =>
        row.tanka !== null && row.tanka !== undefined
          ? formatPrice(row.tanka)
          : '-',
    },
    {
      header: '納入先/納入先住所',
      render: (row) => (
        <div className="text-slate-900 whitespace-normal break-words leading-snug">
          <div>{row.nonyusaki || '-'}</div>
          <div className="text-slate-600">{row.direct_address || '-'}</div>
        </div>
      ),
    },
    {
      header: '仕入先CD/仕入先名',
      render: (row) => (
        <div className="text-slate-900 whitespace-normal break-words leading-snug">
          <div>{row.supplier_disp || '-'}</div>
          <div className="text-slate-600">{row.supplier_name || '-'}</div>
        </div>
      ),
    },
  ];

  return (
    <div className="h-full flex flex-col bg-slate-50">
      <div className="sticky top-0 z-20 page-section">
        <div className="page-header">
          <div>
            <h1 className="page-title">得意先別実績</h1>
            {loadError && (
              <p className="text-xs text-red-600 mt-1">{loadError}</p>
            )}
          </div>
          <div className="page-header-actions">
            <PageSyncMeta groups={['orders']} />
            <button
              type="button"
              onClick={handleRefresh}
              disabled={loading}
              className="flex items-center gap-2 btn-toolbar bg-slate-700 text-white rounded-md hover:bg-slate-800 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg
                className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              更新
            </button>
          </div>
        </div>
      </div>

      <div className="page-section">
        <form onSubmit={handleFilterSubmit} className="space-y-4">
          <div className="flex flex-wrap items-end gap-4">
            <div className="flex-1 min-w-[240px]">
              <div className="flex items-center gap-3 mb-1">
                <label
                  htmlFor="customer-order-history-text-query"
                  className="text-sm font-medium text-slate-700 shrink-0"
                >
                  テキスト検索
                </label>
                <label className="inline-flex items-center gap-1.5 text-sm text-slate-700 shrink-0 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={filters.textExact}
                    onChange={(e) => handleFilterChange('textExact', e.target.checked)}
                    className="rounded border-slate-300"
                  />
                  完全一致
                </label>
              </div>
              <div className="flex gap-2">
                <input
                  id="customer-order-history-text-query"
                  type="text"
                  value={filters.textQuery}
                  onChange={(e) => handleFilterChange('textQuery', e.target.value)}
                  placeholder="得意先 / 製品CD / 製品名 / 寸法（スペース区切り）"
                  className="flex-1 px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500 text-slate-900"
                />
                {filters.textQuery && (
                  <button
                    type="button"
                    onClick={() => handleFilterClear('textQuery')}
                    className="px-3 py-2 text-sm border border-slate-300 rounded-md text-slate-600 hover:bg-slate-50"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                発注日（開始）
              </label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                className="px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500 text-slate-900"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-slate-700 mb-1">
                発注日（終了）
              </label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                className="px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500 text-slate-900"
              />
            </div>

            <div className="flex gap-2">
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium bg-slate-700 text-white rounded-md hover:bg-slate-800"
              >
                検索
              </button>
              <button
                type="button"
                onClick={handleClearAll}
                className="px-4 py-2 text-sm font-medium border border-slate-300 rounded-md text-slate-700 bg-white hover:bg-slate-50"
              >
                クリア
              </button>
            </div>
          </div>
        </form>
      </div>

      <div className="flex-1 overflow-hidden flex">
        <div className="flex-1 overflow-auto bg-white border-t border-slate-200">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                {columns.map((col) => (
                  <th
                    key={col.header}
                    className="sticky top-0 z-10 bg-slate-50 px-3 py-2 text-left text-xs font-semibold text-slate-600 whitespace-nowrap border-b border-slate-200"
                  >
                    {col.header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading && items.length === 0 ? (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="px-4 py-8 text-center text-slate-500"
                  >
                    読み込み中...
                  </td>
                </tr>
              ) : items.length === 0 ? (
                <tr>
                  <td
                    colSpan={columns.length}
                    className="px-4 py-8 text-center text-slate-500"
                  >
                    データがありません
                  </td>
                </tr>
              ) : (
                items.map((row, idx) => (
                  <tr key={`${row.hacchu_no}-${row.product_disp}-${idx}`} className="hover:bg-slate-50">
                    {columns.map((col) => (
                      <td
                        key={col.header}
                        className="px-3 py-2 text-slate-900 align-top"
                      >
                        {col.render(row)}
                      </td>
                    ))}
                  </tr>
                ))
              )}
            </tbody>
          </table>
          {hasMore && !loading && (
            <div ref={observerTarget} className="h-8 flex items-center justify-center">
              {loadingMore && (
                <span className="text-xs text-slate-500">読み込み中...</span>
              )}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default CustomerOrderHistoryPage;
